import { ISorting, TTaskTreatment } from '../../../../../util/varibles/interface';
import { IUnitWhenEdit } from '../EditOperationContext';

export interface TreatmentDragItem {
    id: string;
    task: TTaskTreatment;
}

export type TreatmentDragContainers = Record<string, TreatmentDragItem[]>;

export const getConditionDragId = (taskParentId: string, conditionId: string) => (
    `condition:${taskParentId}:${conditionId}`
);

export function flattenTaskContainers(units: Record<string, IUnitWhenEdit>): TreatmentDragContainers {
    const containers: TreatmentDragContainers = {};

    Object.entries(units).forEach(([unitId, unit]) => {
        const tasks = (unit.tasks || []) as TTaskTreatment[];
        containers[unitId] = tasks.map((task) => ({ id: task.group_id, task }));

        tasks.forEach((task) => {
            const { sub_tasks: subTasks } = task as ISorting;
            if (!subTasks || Array.isArray(subTasks)) return;

            Object.entries(subTasks).forEach(([conditionId, subTasksByCondition]) => {
                containers[getConditionDragId(task.group_id, conditionId)] = subTasksByCondition.map((subTask) => ({
                    id: subTask.group_id,
                    task: subTask,
                }));
            });
        });
    });

    return containers;
}

export function rebuildUnitsFromContainers(
    units: Record<string, IUnitWhenEdit>,
    containers: TreatmentDragContainers,
): Record<string, IUnitWhenEdit> {
    const rebuildTask = (task: TTaskTreatment): TTaskTreatment => {
        const sortingTask = task as ISorting;
        if (!sortingTask.sub_tasks || Array.isArray(sortingTask.sub_tasks)) return task;

        return {
            ...sortingTask,
            sub_tasks: Object.keys(sortingTask.sub_tasks).reduce((result: ISorting['sub_tasks'], conditionId) => {
                result[conditionId] = (containers[getConditionDragId(task.group_id, conditionId)] || [])
                    .map((item) => item.task);
                return result;
            }, {}),
        };
    };

    return Object.keys(units).reduce((result: Record<string, IUnitWhenEdit>, unitId) => {
        result[unitId] = {
            ...units[unitId],
            tasks: (containers[unitId] || []).map((item) => rebuildTask(item.task)),
        };
        return result;
    }, {});
}

export function findTaskById(
    units: Record<string, IUnitWhenEdit>,
    taskId: string,
): TTaskTreatment | undefined {
    for (const unit of Object.values(units)) {
        for (const task of (unit.tasks || []) as TTaskTreatment[]) {
            if (task.group_id === taskId) return task;

            const { sub_tasks: subTasks } = task as ISorting;
            if (!subTasks || Array.isArray(subTasks)) continue;

            const nestedTask = Object.values(subTasks).flat().find((item) => item.group_id === taskId);
            if (nestedTask) return nestedTask;
        }
    }

    return undefined;
}

export function findTaskUnitId(
    units: Record<string, IUnitWhenEdit>,
    taskId: string,
): string | undefined {
    return Object.keys(units).find((unitId) => {
        const tasks = (units[unitId].tasks || []) as TTaskTreatment[];
        return tasks.some((task) => {
            if (task.group_id === taskId) return true;

            const { sub_tasks: subTasks } = task as ISorting;
            return Boolean(
                subTasks
                && !Array.isArray(subTasks)
                && Object.values(subTasks).flat().some((item) => item.group_id === taskId)
            );
        });
    });
}
